import {
  DestroyRef,
  inject,
  Injectable,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ComponentStore } from '@ngrx/component-store';
import * as Sentry from '@sentry/angular';
import {
  FileTypeIconsEnum,
  getFileTypeIconFromExtension,
} from '@serious-system';
import { DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS } from '@shared';
import {
  catchError,
  EMPTY,
  finalize,
  forkJoin,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import * as fromGenerated from '../../../../_generated';
import type { FileUploadedEvent } from '../../../../analytics/analytics.events';
import { AnalyticsService } from '../../../../analytics/analytics.service';
import { Mappers } from './file-upload-v2.helpers';
import {
  FileUploadState,
  FileUploadStateFile,
} from './file-upload-v2.interfaces';

export const initialState: FileUploadState = {
  files: {},
  error: null,
  valid: true,
  loading: false,
  uploaded: false,
};

/**
 * Options for {@link FileUploadV2Store.configure}. Every knob is optional and
 * defaults to today's chat behavior when omitted, so an unconfigured store
 * (the chat composer / playground) is byte-identical to before this PR.
 */
export interface FileUploadV2ConfigureOptions {
  /** Accepted extensions; defaults to the image-inclusive chat list. */
  supportedExtensions?: string[];
  /**
   * A single transaction id shared by every file staged on this store. The
   * skill builder needs one prefix per modal session (the server HEADs
   * `${transactionId}/${documentId}/${filename}`); chat mints one per file.
   */
  transactionId?: string;
  /** Analytics `from` for uploads on this store; defaults to `chat_prompt_input`. */
  uploadFrom?: FileUploadedEvent['from'];
}

@Injectable()
export class FileUploadV2Store extends ComponentStore<FileUploadState> {
  private readonly fileUploadService = inject(fromGenerated.FileUploadService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Accepted extensions. Configurable via {@link configure}; defaults to the
   * chat list. Mutable (not `readonly`) so `configure`/`reset` can re-point it.
   */
  private supportedExtensions: string[] = [
    ...DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS,
  ];

  /**
   * When set (via {@link configure}), overrides the per-file transaction id on
   * every staged file so they share one S3 prefix. `null` = chat default
   * (per-file uuidv4 from the mapper survives).
   */
  private transactionId: string | null = null;

  /**
   * The value {@link uploadValidFiles} resets `uploadFrom` to after each batch.
   * Configured together with the live value so the FIRST batch is labelled
   * correctly too (re-targeting the reset alone would mislabel a zone's first
   * batch as `chat_prompt_input`).
   */
  private defaultUploadFrom: FileUploadedEvent['from'] = 'chat_prompt_input';

  /**
   * Single-flight lock for {@link uploadValidFiles}: at most one batch runs per
   * store at a time; a pick landing mid-flight is drained after the current
   * batch completes rather than starting a parallel run.
   */
  private isUploading = false;

  /**
   * Fired by {@link reset} to abort the in-flight upload batch. Without this, a
   * hung request keeps the `isUploading` lock stuck `true`, so every batch added
   * after a reset (switching chat, or leaving/re-entering compliance) returns
   * `EMPTY` and stalls forever.
   */
  private readonly cancelUploads$ = new Subject<void>();

  /**
   * Tracks the source of file uploads for analytics
   */
  public uploadFrom: FileUploadedEvent['from'] = 'chat_prompt_input';

  constructor() {
    super(initialState);
  }

  /**
   * Opt-in configuration for the skill builder. Overwrite semantics and
   * idempotent under identical args (the compliance three-slot mount configures
   * the same page-owned store on each slot's `ngOnInit`, so ×3 must equal ×1).
   * Chat/playground never call this and stay on the defaults.
   */
  public configure(options: FileUploadV2ConfigureOptions): void {
    this.supportedExtensions = options.supportedExtensions
      ? [...options.supportedExtensions]
      : [...DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS];
    this.transactionId = options.transactionId ?? null;
    const from = options.uploadFrom ?? 'chat_prompt_input';
    this.uploadFrom = from;
    this.defaultUploadFrom = from;
  }

  /**
   * Sets the upload source for analytics tracking
   */
  public setUploadFrom(from: FileUploadedEvent['from']): void {
    this.uploadFrom = from;
  }

  public getFiles() {
    return Object.values(this.state().files);
  }

  /**
   * Edit-mode seeding for persisted documents: adds `upload-done` entries with
   * no `file` and `transactionId: null` (they never upload and never enter
   * `documentsToAdd`). Idempotent by document uuid — safe under the compliance
   * shared-store mirror, where three slots seed the same list. Legal before
   * {@link configure} (done entries skip validation, so the default extension
   * list can't flip them invalid).
   */
  public seedPersistedFiles(
    docs: { uuid: string; name: string; extension: string; size: number }[],
  ): void {
    this.patchState((state) => {
      const files = { ...state.files };
      for (const doc of docs) {
        // Idempotent: never overwrite an entry already in the store (id is
        // identity — an in-flight entry must not be clobbered into seeded-done).
        if (doc.uuid in files) {
          continue;
        }
        files[doc.uuid] = {
          transactionId: null,
          uuid: doc.uuid,
          name: doc.name,
          extension: doc.extension,
          size: doc.size,
          status: 'upload-done',
          error: null,
        };
      }
      return Mappers.toValidatedState(
        { ...state, files },
        this.supportedExtensions,
      );
    });
  }

  private updateFile(
    fileUpdate: Pick<FileUploadStateFile, 'uuid'> &
      Partial<Omit<FileUploadStateFile, 'uuid' | 'file'>>,
  ) {
    this.patchState((state) => {
      // No-op when the entry is gone: a late upload callback (success OR the
      // catchError failure path) must never resurrect a chip that was removed or
      // reset while the upload was in flight. Spreading an absent entry would
      // otherwise mint a malformed partial file and re-block Save.
      if (!(fileUpdate.uuid in state.files)) {
        return state;
      }
      return Mappers.toValidatedState(
        {
          ...state,
          files: {
            ...state.files,
            [fileUpdate.uuid]: {
              ...state.files[fileUpdate.uuid],
              ...fileUpdate,
            },
          },
        },
        this.supportedExtensions,
      );
    });
  }

  public addFiles(newFiles: FileUploadStateFile[]) {
    // When a shared transaction id is configured, overwrite it on every staged
    // file BEFORE validation so all files land under one S3 prefix. This is the
    // single staging chokepoint (the picker and drag-drop both flow through
    // `addFiles`); the mapper — which runs in the callers, with no store config
    // access — is the wrong place, so the per-file uuidv4 is replaced here.
    const staged = this.transactionId
      ? newFiles.map((file) => ({ ...file, transactionId: this.transactionId }))
      : newFiles;

    this.patchState((state) =>
      Mappers.toValidatedState(
        {
          ...state,
          files: {
            ...state.files,
            ...staged.reduce(
              (files, file) => ({ ...files, [file.uuid]: file }),
              {},
            ),
          },
        },
        this.supportedExtensions,
      ),
    );

    // Store-owned upload driver: staging is the only mutation that introduces
    // `upload-pending` entries, so kick uploads here (fire-and-forget). The
    // directive no longer drives uploads, so directive teardown (e.g. STT
    // replacing the chat attach button) can never abort an in-flight POST.
    this.uploadValidFiles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  /**
   * Uploads the files that are in the state and are in the `upload-pending`
   * status. Single-flight with drain: if a batch is already running, returns
   * `EMPTY` (a harmless no-op for concurrent callers such as the drag-drop
   * path) — the running batch's `finalize` re-checks for newly-pending files
   * and runs again, so a pick landing mid-flight is serialized, never dropped.
   */
  public uploadValidFiles(): Observable<unknown> {
    if (this.isUploading) {
      return EMPTY;
    }

    const validFiles = this.getFiles().filter(
      (
        file,
      ): file is FileUploadStateFile & {
        file: File;
        status: 'upload-pending';
        transactionId: string;
      } =>
        !!file.file &&
        file.status === 'upload-pending' &&
        file.transactionId !== null,
    );

    if (!validFiles.length) {
      return EMPTY;
    }

    this.isUploading = true;
    return forkJoin(validFiles.map((file) => this.uploadFile(file))).pipe(
      // Reset the upload from to the configured default (chat_prompt_input when
      // unconfigured).
      tap(() => {
        this.setUploadFrom(this.defaultUploadFrom);
      }),
      // `reset()` fires this to abort the in-flight batch (unsubscribing the
      // HTTP requests); placed before `finalize` so the lock is still released
      // and the drain still runs on cancellation.
      takeUntil(this.cancelUploads$),
      finalize(() => {
        this.isUploading = false;
        // Drain: a pick that landed mid-flight staged new pending files.
        const hasPending = this.getFiles().some(
          (file) => !!file.file && file.status === 'upload-pending',
        );
        if (hasPending) {
          this.uploadValidFiles()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }
      }),
    );
  }
  private uploadFile(
    file: Omit<FileUploadStateFile, 'file' | 'transactionId'> & {
      file: File;
      status: 'upload-pending';
      transactionId: string;
    },
  ) {
    this.updateFile({
      uuid: file.uuid,
      status: 'upload-ongoing',
    });

    return this.fileUploadService
      .fileUploadControllerUploadFileV1(
        file.uuid,
        file.transactionId,
        file.file,
      )
      .pipe(
        // A destroyed store must not late-patch state (the store is page-scoped,
        // so this ties HTTP to the owner's lifetime, not the directive's).
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          /**
           * We only want to update the state if the file uuid is still in the state.
           */
          if (file.uuid in this.state().files) {
            this.updateFile({
              uuid: file.uuid,
              status: 'upload-done',
            });
          }
          // Track analytics event for successful file upload
          this.analyticsService
            .sendFileUploadedEvent({
              from: this.uploadFrom,
              fileExtension: file.extension,
            })
            .subscribe();
          // Image-specific tracking: a dedicated event for the
          // image funnel, fired only for chat-composer image uploads
          // (Skill Builder image upload is out of scope).
          if (
            this.uploadFrom !== 'skill_builder' &&
            getFileTypeIconFromExtension(file.extension) ===
              FileTypeIconsEnum.image
          ) {
            this.analyticsService
              .sendImageAttachedEvent({
                extension: file.extension,
                sizeBytes: file.file.size,
                origin: 'composer',
              })
              .subscribe();
          }
        }),
        catchError((error: Error) => {
          Sentry.captureException(error, {
            tags: {
              fileId: file.uuid,
              transactionId: file.transactionId,
            },
          });
          this.updateFile({
            uuid: file.uuid,
            status: 'upload-failed',
            error: 'Error uploading file',
          });
          return of('Error uploading file');
        }),
      );
  }

  public removeFile(id: string) {
    this.removeFiles([id]);
  }
  public removeFiles(ids: string[]) {
    this.patchState((state) => {
      const idsToRemove = new Set(ids);
      const files = Object.fromEntries(
        Object.entries(state.files).filter(([id]) => !idsToRemove.has(id)),
      );

      return Mappers.toValidatedState(
        {
          ...state,
          files,
        },
        this.supportedExtensions,
      );
    });
  }

  /**
   * Restores factory defaults — state AND configuration. Wiping the configured
   * extension list / transactionId / uploadFrom is deliberate: the store owner
   * (the builder page / a factory-created zone store) resets on teardown and
   * re-configures on remount, so config is re-established fresh; leaving it
   * sticky would let a stray `configure()` permanently change chat validation.
   */
  public reset() {
    this.supportedExtensions = [...DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS];
    this.transactionId = null;
    this.uploadFrom = 'chat_prompt_input';
    this.defaultUploadFrom = 'chat_prompt_input';
    // Clear state FIRST so the aborted batch's finalize-drain finds no pending
    // files to re-run, then cancel the in-flight batch (its `takeUntil`
    // completes → `finalize` releases `isUploading`) and release the lock
    // explicitly as a belt (covers the case where no batch was active but the
    // lock somehow stuck). Without this, a hung request would stall every later
    // upload on this store.
    this.patchState(initialState);
    this.cancelUploads$.next();
    this.isUploading = false;
  }
}

/**
 * Creates a {@link FileUploadV2Store} imperatively, without a DI provider. The
 * skill builder must NOT provide the store via DI (a bare
 * `inject(FileUploadV2Store)` in the builder tree throws `NullInjectorError` by
 * construction — the trap fails loud in every zone instead of shipping a
 * compliance-only bug). The store's own `inject()`s run in the caller's
 * injection context and its `ngOnDestroy` is tied to the caller's `DestroyRef`,
 * so ownership is literal: the store dies with whoever created it.
 *
 * Chat pages keep their `providers: [FileUploadV2Store]` and never use this.
 */
export function createFileUploadV2Store(injector: Injector): FileUploadV2Store {
  return runInInjectionContext(injector, () => {
    const store = new FileUploadV2Store();
    inject(DestroyRef).onDestroy(() => store.ngOnDestroy());
    return store;
  });
}
